/**
 * Data Flow Validator - Simple validation without complex fallbacks
 * 
 * This replaces the complex MonteCarloResultsValidator system with 
 * straightforward validation that fails fast rather than attempting corrections.
 */

import { SimulationResult, ADTScenario } from '@/types/adt';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export class DataFlowValidator {
  
  /**
   * Validate simulation result data integrity
   */
  static validateSimulationResult(
    result: SimulationResult, 
    scenario: ADTScenario
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 1. Required fields validation
    if (typeof result.successRate !== 'number' || isNaN(result.successRate)) {
      errors.push('Missing or invalid success rate');
    }
    
    if (typeof result.medianOutcome !== 'number' || isNaN(result.medianOutcome)) {
      errors.push('Missing or invalid median outcome');
    }
    
    if (!result.timelineData || !Array.isArray(result.timelineData)) {
      errors.push('Missing or invalid timeline data');
    }
    
    if (!result.chartData || !Array.isArray(result.chartData)) {
      errors.push('Missing or invalid chart data');
    }

    // 2. Data consistency validation
    if (result.timelineData && result.timelineData.length > 0) {
      const firstPoint = result.timelineData[0];
      const lastPoint = result.timelineData[result.timelineData.length - 1];
      
      // Check age progression
      if (firstPoint.age && lastPoint.age && lastPoint.age <= firstPoint.age) {
        errors.push('Timeline ages are not progressing correctly');
      }
      
      // Check for missing portfolio values
      const missingValues = result.timelineData.filter(point => 
        typeof point.portfolioValue !== 'number' || isNaN(point.portfolioValue)
      );
      if (missingValues.length > 0) {
        errors.push(`${missingValues.length} timeline points have invalid portfolio values`);
      }
    }

    // 3. Financial logic validation - Enhanced with proportional validation
    const initialAssets = this.calculateInitialAssets(scenario);
    
    // More reasonable validation - negative outcomes can be legitimate in some scenarios
    const reasonableThreshold = Math.max(-500000, -initialAssets * 5);
    if (result.medianOutcome < reasonableThreshold) {
      warnings.push(`Median outcome of ${result.medianOutcome.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })} suggests high portfolio depletion risk`);
    }
    
    if (result.successRate < 0 || result.successRate > 100) {
      errors.push('Success rate must be between 0 and 100');
    }

    // 4. Percentile validation
    if (result.percentiles) {
      const { p10, p25, p50, p75, p90 } = result.percentiles;
      
      if (p10 > p25 || p25 > p50 || p50 > p75 || p75 > p90) {
        errors.push('Percentiles are not in ascending order');
      }
    }

    // 5. Scenario alignment validation
    const currentAge = scenario.basicInformation?.currentAge;
    if (currentAge && result.timelineData && result.timelineData.length > 0) {
      const firstTimelineAge = result.timelineData[0].age;
      if (Math.abs(firstTimelineAge - currentAge) > 2) {
        warnings.push(`Timeline starting age (${firstTimelineAge}) doesn't match scenario current age (${currentAge})`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate chart data consistency
   */
  static validateChartData(chartData: any[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!Array.isArray(chartData) || chartData.length === 0) {
      errors.push('Chart data must be a non-empty array');
      return { isValid: false, errors, warnings };
    }

    // Check for required fields
    const requiredFields = ['year', 'age', 'portfolioValue'];
    const missingFields = new Set<string>();

    chartData.forEach((point, index) => {
      requiredFields.forEach(field => {
        if (!(field in point) || typeof point[field] !== 'number' || isNaN(point[field])) {
          missingFields.add(field);
        }
      });
    });

    if (missingFields.size > 0) {
      errors.push(`Chart data missing required fields: ${Array.from(missingFields).join(', ')}`);
    }

    // Check age/year progression
    let prevAge = -1;
    let prevYear = -1;
    
    chartData.forEach((point, index) => {
      if (point.age <= prevAge && index > 0) {
        warnings.push(`Age not progressing correctly at index ${index}`);
      }
      if (point.year <= prevYear && index > 0) {
        warnings.push(`Year not progressing correctly at index ${index}`);
      }
      
      prevAge = point.age;
      prevYear = point.year;
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Calculate total initial assets from scenario
   */
  private static calculateInitialAssets(scenario: ADTScenario): number {
    if (!scenario.assets) return 0;
    
    return (scenario.assets.superBalance || 0) + 
           (scenario.assets.liquidInvestments || 0) + 
           (scenario.assets.cashAtBank || 0);
  }

  /**
   * Validate that simulation parameters are reasonable
   */
  static validateScenarioParameters(scenario: ADTScenario): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Age validation
    const currentAge = scenario.basicInformation?.currentAge;
    const retirementAge = scenario.basicInformation?.retirementAge;
    const lifeExpectancy = scenario.lifeExpectancyAssumptions?.expectedEndOfLife;

    if (!currentAge || currentAge < 18 || currentAge > 100) {
      errors.push('Current age must be between 18 and 100');
    }

    if (!retirementAge || retirementAge <= currentAge || retirementAge > 100) {
      errors.push('Retirement age must be greater than current age and less than 100');
    }

    if (lifeExpectancy && lifeExpectancy <= retirementAge) {
      errors.push('Life expectancy must be greater than retirement age');
    }

    // Asset validation
    const totalAssets = this.calculateInitialAssets(scenario);
    if (totalAssets < 0) {
      errors.push('Total assets cannot be negative');
    }

    // Strategy validation
    const growthAllocation = scenario.strategy?.allocationToGrowth;
    if (growthAllocation !== undefined && (growthAllocation < 0 || growthAllocation > 100)) {
      errors.push('Growth allocation must be between 0 and 100');
    }

    const withdrawalRate = scenario.strategy?.withdrawalRate;
    if (withdrawalRate !== undefined && (withdrawalRate < 0 || withdrawalRate > 20)) {
      errors.push('Withdrawal rate must be between 0 and 20');
    }

    // Warning checks
    if (totalAssets === 0 && currentAge > 30) {
      warnings.push('No initial assets detected for someone over 30');
    }

    if (growthAllocation !== undefined && growthAllocation > 90 && currentAge > 55) {
      warnings.push('Very high growth allocation for someone approaching retirement');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}